export type MaterialPurityStatus = "unknown" | "estimated" | "tested";
export type MaterialCompositionStatus = "unknown" | "reported" | "field_test" | "laboratory_test" | "partial";
export type ComponentDetectionStatus = "tested_detected" | "reported" | "suspected" | "not_detected" | "unknown";
export type ComponentConfidence = "baixa" | "moderada" | "alta";

export interface MaterialComponent {
  id?: string;
  name: string;
  kind: "active" | "adulterant" | "diluent" | "contaminant" | "unknown" | "other";
  status: ComponentDetectionStatus;
  estimated_percent?: number | null;
  confidence: ComponentConfidence;
  mechanism?: string;
  clinical_effects?: string[];
  risk_axes?: string[];
  notes?: string;
  source_label?: string;
  source_url?: string;
  evidence_note?: string;
}
export interface MaterialProfile {
  id?: string;
  owner_id?: string;
  patient_id?: string | null;
  substance_id?: string | null;
  canonical_substance_name: string;
  profile_name: string;
  sample_label?: string | null;
  sample_kind?: string;
  material_form?: string | null;
  source_context?: string | null;
  source_region?: string | null;
  purity_status: MaterialPurityStatus;
  purity_percent?: number | null;
  purity_method?: string | null;
  purity_assessed_at?: string | null;
  composition_status: MaterialCompositionStatus;
  composition_method?: string | null;
  composition_tested_at?: string | null;
  composition_panel?: string | null;
  composition_lab?: string | null;
  composition_coverage_notes?: string | null;
  composition_confidence: ComponentConfidence;
  composition: MaterialComponent[];
  risk_summary?: string | null;
  clinical_modifiers?: Record<string, unknown>;
  notes?: string | null;
  is_active?: boolean;
  updated_at?: string;
}

export interface MaterialRiskInterpretation {
  title: string;
  summary: string;
  activeMassMg: number | null;
  confidence: ComponentConfidence;
  evidenceBasis: string[];
  potentialEffects: string[];
  specificRisks: string[];
  redFlags: string[];
  limitations: string[];
  compositionFindings: Array<{
    name: string;
    status: ComponentDetectionStatus;
    confidence: ComponentConfidence;
    mechanism: string;
    clinicalEffects: string[];
    riskAxes: string[];
    sourceUrl?: string;
    evidenceNote?: string;
  }>;
}

export interface KnownComponentPreset {
  id: string;
  name: string;
  kind: MaterialComponent["kind"];
  mechanism: string;
  clinical_effects: string[];
  risk_axes: string[];
  evidence_note: string;
  source_url?: string;
}

export const KNOWN_MATERIAL_COMPONENTS: KnownComponentPreset[] = [
  {
    id: "levamisole",
    name: "Levamisol",
    kind: "adulterant",
    mechanism: "Imunomodulação associada a vasculopatia/vasculite e neutropenia em exposição relevante; a presença no lote precisa ser estabelecida separadamente.",
    clinical_effects: ["púrpura retiforme/lesões cutâneas", "vasculopatia/vasculite", "leucopenia/neutropenia; raramente agranulocitose", "possível comprometimento renal ou neurológico em quadros sistêmicos"],
    risk_axes: ["hematológico", "imunológico", "cutâneo-vascular", "renal"],
    evidence_note: "A evidência descreve casos clínicos/séries/revisões de exposição associada; não permite inferir presença, concentração ou probabilidade neste material sem análise.",
    source_url: "https://pubmed.ncbi.nlm.nih.gov/41330036/",
  },
  {
    id: "phenacetin",
    name: "Fenacetina",
    kind: "adulterant",
    mechanism: "Analgésico historicamente identificado em análises de amostras. Toxicidade depende de exposição e concentração; presença isolada não quantifica risco individual.",
    clinical_effects: ["potencial toxicidade renal/hepática em exposição suficiente ou repetida"],
    risk_axes: ["renal", "hepático", "toxicidade crônica"],
    evidence_note: "Prevalência varia por país, período e programa de drug-checking; não inferir presença sem registro de testagem ou relato identificado.",
    source_url: "https://pmc.ncbi.nlm.nih.gov/articles/PMC9739917/",
  },
  {
    id: "caffeine",
    name: "Cafeína",
    kind: "adulterant",
    mechanism: "Antagonismo de receptores de adenosina; se presente em quantidade relevante pode somar ativação autonômica, não devendo ser confundida com aumento de pureza da cocaína.",
    clinical_effects: ["ansiedade/agitação", "insônia", "tremor/palpitações", "aumento adicional de FC/PA em pessoas suscetíveis"],
    risk_axes: ["simpaticomimético", "cardiovascular", "sono", "ansiedade"],
    evidence_note: "É possível em algumas amostras; frequência de estudos estrangeiros não estima a composição deste lote.",
    source_url: "https://pmc.ncbi.nlm.nih.gov/articles/PMC12672436/",
  },
  {
    id: "benzocaine",
    name: "Benzocaína",
    kind: "adulterant",
    mechanism: "Anestésico local; toxicidade sistêmica depende da quantidade absorvida. Em exposição clinicamente significativa, benzocaína é associada a meta-hemoglobinemia.",
    clinical_effects: ["cianose ou hipoxemia desproporcional", "cefaleia/dispneia em meta-hemoglobinemia clinicamente significativa"],
    risk_axes: ["meta-hemoglobinemia", "hipóxia"],
    evidence_note: "Não prevê sintomas se presente apenas em traços; concentração e via de exposição são essenciais.",
    source_url: "https://pmc.ncbi.nlm.nih.gov/articles/PMC9739917/",
  },
  {
    id: "lidocaine",
    name: "Lidocaína",
    kind: "adulterant",
    mechanism: "Anestésico local com bloqueio de canais de sódio; toxicidade sistêmica em exposição significativa pode envolver SNC e condução cardíaca.",
    clinical_effects: ["sintomas neurológicos em toxicidade significativa", "convulsão ou arritmia em toxicidade sistêmica importante"],
    risk_axes: ["neurológico", "convulsão", "cardíaco"],
    evidence_note: "Identificação numa amostra não revela, por si só, concentração absorvida nem toxicidade esperada.",
    source_url: "https://pmc.ncbi.nlm.nih.gov/articles/PMC12672436/",
  },
  {
    id: "procaine",
    name: "Procaína",
    kind: "adulterant",
    mechanism: "Anestésico local; efeito/toxicidade dependem da concentração e exposição. Pode acrescentar risco neurológico/cardiovascular em níveis sistêmicos relevantes.",
    clinical_effects: ["sintomas neurológicos em toxicidade sistêmica", "alterações de condução/arritmia em toxicidade significativa"],
    risk_axes: ["neurológico", "cardíaco"],
    evidence_note: "Estudos de drug-checking mostram variação temporal entre amostras; não extrapolar para um lote sem análise.",
    source_url: "https://pmc.ncbi.nlm.nih.gov/articles/PMC12672436/",
  },
  {
    id: "acetaminophen",
    name: "Paracetamol",
    kind: "adulterant",
    mechanism: "Analgésico/antipirético; a preocupação clínica muda conforme massa total e exposição cumulativa.",
    clinical_effects: ["hepatotoxicidade em exposição excessiva"],
    risk_axes: ["hepático"],
    evidence_note: "Presença e quantidade precisam de confirmação; não estimar carga hepática sem concentração/quantificação.",
    source_url: "https://pmc.ncbi.nlm.nih.gov/articles/PMC9739917/",
  },
  {
    id: "opioid_unknown",
    name: "Opioide não identificado (relato/teste)",
    kind: "contaminant",
    mechanism: "Se confirmado, agonismo opioide pode produzir depressão respiratória; não deve ser presumido por ausência de teste nem pela aparência do material.",
    clinical_effects: ["sonolência/rebaixamento de consciência", "respiração lenta/irregular", "risco de overdose respiratória"],
    risk_axes: ["depressão respiratória", "SNC"],
    evidence_note: "Marcar como detectado apenas com resultado analítico ou relato documentado. Não assumir prevalência local.",
    source_url: "https://pmc.ncbi.nlm.nih.gov/articles/PMC9739917/",
  },
];

const GENERAL_COMPONENT_PRESETS: KnownComponentPreset[] = [
  {
    id: "unknown-active",
    name: "Princípio ativo não identificado",
    kind: "unknown",
    mechanism: "Identidade química desconhecida: não há base para estimar farmacologia, potência, metabolismo ou interações específicas.",
    clinical_effects: ["efeitos clínicos imprevisíveis conforme identidade e exposição"],
    risk_axes: ["identidade/composição incerta", "interações não caracterizadas"],
    evidence_note: "Categoria de incerteza, não substância química. Não deve ser interpretada como presença confirmada de um agente específico.",
  },
  {
    id: "unknown-contaminant",
    name: "Contaminante não caracterizado",
    kind: "contaminant",
    mechanism: "Componente não identificado; nenhum mecanismo deve ser inferido sem análise ou fonte explícita.",
    clinical_effects: ["risco não quantificável sem identificação e concentração"],
    risk_axes: ["composição incerta"],
    evidence_note: "Usar apenas para registrar incerteza ou resultado de painel analítico incompleto.",
  },
  {
    id: "potent-opioid-unknown",
    name: "Opioide potente não identificado (ex.: fentanil/nitazeno)",
    kind: "contaminant",
    mechanism: "Se realmente presente, agonismo opioide pode causar depressão respiratória. A presença precisa ser documentada; não é presumida por nome, aparência ou origem.",
    clinical_effects: ["sedação/rebaixamento de consciência", "respiração lenta/irregular", "risco de overdose respiratória se a exposição for relevante"],
    risk_axes: ["depressão respiratória", "SNC"],
    evidence_note: "Registrar como suspeito, relatado ou detectado de acordo com a evidência real. Não afirmar prevalência para a amostra sem análise.",
    source_url: "https://pmc.ncbi.nlm.nih.gov/articles/PMC9739917/",
  },
  {
    id: "sedative-unknown",
    name: "Sedativo/benzodiazepínico não identificado",
    kind: "contaminant",
    mechanism: "Se confirmado, efeito depressor central pode ser prolongado e interagir com álcool, opioides e outros sedativos.",
    clinical_effects: ["sedação prolongada", "ataxia/amnesia", "depressão respiratória em combinações ou exposições relevantes"],
    risk_axes: ["SNC", "depressão respiratória", "duração incerta"],
    evidence_note: "Usar apenas quando o dado for explicitamente relatado ou demonstrado; não inferir a partir de sonolência isolada.",
  },
  {
    id: "stimulant-unknown",
    name: "Estimulante/anfetamínico não identificado",
    kind: "contaminant",
    mechanism: "Se confirmado, pode somar atividade catecolaminérgica; meia-vida e toxicidade dependem da molécula, da concentração e da via.",
    clinical_effects: ["taquicardia/hipertensão", "agitação/insônia", "ansiedade/paranoia; convulsão/hipertermia em toxicidade grave"],
    risk_axes: ["simpaticomimético", "cardiovascular", "neuropsiquiátrico", "sono"],
    evidence_note: "Identidade e concentração precisam de confirmação. Não se deve assumir equivalência com cocaína ou anfetamina sem análise.",
  },
];
export const ALL_MATERIAL_COMPONENT_PRESETS = [...KNOWN_MATERIAL_COMPONENTS, ...GENERAL_COMPONENT_PRESETS];

export function getMaterialComponentPresets(canonicalSubstanceName: string): KnownComponentPreset[] {
  const name = normalizeMaterialToken(canonicalSubstanceName);
  if (/cocaina|cocaine|crack/.test(name)) return KNOWN_MATERIAL_COMPONENTS;
  if (/morfina|morphine|opio|heroina|heroin|fentan|fentanyl|oxicod|oxycod|metadon|methadone|bupren|tramad/.test(name)) {
    return GENERAL_COMPONENT_PRESETS.filter((item) => ["unknown-active", "unknown-contaminant", "potent-opioid-unknown", "sedative-unknown"].includes(item.id));
  }
  if (/anfet|amphetamine|metilfen|methylphen|cathinon|estimul|mdma|metanfet/.test(name)) {
    return [...GENERAL_COMPONENT_PRESETS.filter((item) => ["unknown-active", "unknown-contaminant", "stimulant-unknown"].includes(item.id)), ...KNOWN_MATERIAL_COMPONENTS.filter((item) => item.id === "caffeine")];
  }
  if (/clonaz|diazep|alpraz|loraz|benzodia|zolpid|zopic|sedativ/.test(name)) {
    return GENERAL_COMPONENT_PRESETS.filter((item) => ["unknown-active", "unknown-contaminant", "sedative-unknown", "potent-opioid-unknown"].includes(item.id));
  }
  return GENERAL_COMPONENT_PRESETS.filter((item) => ["unknown-active", "unknown-contaminant"].includes(item.id));
}

export const MATERIAL_COMPOSITION_STATUS_OPTIONS: Array<{ value: ComponentDetectionStatus; label: string }> = [
  { value: "tested_detected", label: "Detectado em análise" },
  { value: "reported", label: "Relatado por fonte/paciente" },
  { value: "suspected", label: "Suspeito, não confirmado" },
  { value: "not_detected", label: "Não detectado pelo método" },
  { value: "unknown", label: "Desconhecido" },
];

export function normalizeMaterialToken(value: unknown) {
  return String(value ?? "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, " ").trim();
}

function profileIsCocaine(profile: MaterialProfile | null | undefined) {
  return /cocaina|cocaine|crack/.test(normalizeMaterialToken(profile?.canonical_substance_name));
}

function unique(items: string[]) {
  return [...new Set(items.filter(Boolean))];
}

/** Conservative clinical/harm-reduction interpretation: no composition inferred from appearance. */
export function interpretMaterialProfile(
  profile: MaterialProfile | null | undefined,
  powderMassMg?: number | null,
): MaterialRiskInterpretation | null {
  if (!profile) return null;
  const purity = profile.purity_status !== "unknown" && Number(profile.purity_percent) > 0 && Number(profile.purity_percent) <= 100
    ? Number(profile.purity_percent)
    : null;
  const mass = Number(powderMassMg);
  const activeMassMg = profileIsCocaine(profile) && Number.isFinite(mass) && mass > 0 && purity != null
    ? mass * purity / 100
    : null;

  const compositionFindings = (Array.isArray(profile.composition) ? profile.composition : [])
    .filter((component) => component.status !== "not_detected" && component.status !== "unknown")
    .map((component) => ({
      name: component.name,
      status: component.status,
      confidence: component.confidence,
      mechanism: component.mechanism ?? "Mecanismo não detalhado na base para este componente.",
      clinicalEffects: Array.isArray(component.clinical_effects) ? component.clinical_effects : [],
      riskAxes: Array.isArray(component.risk_axes) ? component.risk_axes : [],
      sourceUrl: component.source_url,
      evidenceNote: component.evidence_note,
    }));

  const fullPanelEvidence = profile.composition_status === "laboratory_test" &&
    !!profile.composition_method?.trim() &&
    !!profile.composition_panel?.trim() &&
    !!profile.composition_tested_at &&
    Array.isArray(profile.composition) &&
    profile.composition.length > 0 &&
    profile.composition.every((component) => component.status === "tested_detected" || component.status === "not_detected");
  const isLabVerified = fullPanelEvidence &&
    compositionFindings.length > 0 &&
    compositionFindings.every((component) => component.status === "tested_detected");
  const evidenceBasis = [
    purity == null ? "Pureza desconhecida/não quantificada; massa ativa não calculada." :
      "Pureza " + purity + "% (" + (profile.purity_status === "tested" ? "análise informada" : "estimativa informada") + "); massa ativa é somente estimativa matemática se a massa do material também estiver disponível.",
    "Composição: " + profile.composition_status + "; confiança declarada: " + profile.composition_confidence + ".",
    profile.purity_method ? "Método/origem da pureza: " + profile.purity_method + "." : "",
    profile.composition_method ? "Método da composição: " + profile.composition_method + "." : "",
    profile.composition_panel ? "Painel/escopo pesquisado: " + profile.composition_panel + "." : "",
    profile.composition_lab ? "Laboratório/fonte: " + profile.composition_lab + "." : "",
    profile.composition_tested_at ? "Data da análise de composição: " + String(profile.composition_tested_at).slice(0,10) + "." : "",
    profile.composition_coverage_notes ? "Limites de cobertura: " + profile.composition_coverage_notes + "." : "",
    profile.source_context ? "Contexto/origem declarado: " + profile.source_context + "." : "",
  ].filter(Boolean);

  const potentialEffects = profileIsCocaine(profile)
    ? [
        "estimulação simpaticomimética: taquicardia, elevação pressórica, sudorese e tremor",
        "ansiedade, agitação, hipervigilância e paranoia podem ocorrer; não são inferíveis como certas a partir da pureza isolada",
        "dor torácica, hipertermia, convulsão, arritmia ou evento isquêmico são desfechos de gravidade, não uma previsão individual",
      ]
    : [];

  const specificRisks = compositionFindings.flatMap((component) => component.riskAxes.map((risk) => {
    const status = component.status === "tested_detected" ? "componente detectado" :
      component.status === "reported" ? "componente relatado, não confirmado independentemente" :
      "componente suspeito, não confirmado";
    return risk + ": " + component.name + " (" + status + "; confiança " + component.confidence + ").";
  }));

  const redFlags = unique(compositionFindings.flatMap((component) => {
    if (/levamisol/i.test(component.name)) return ["lesões purpúricas/retiformes, necrose cutânea, febre ou sinais de infecção: considerar neutropenia/vasculopatia e avaliação laboratorial"];
    if (/opioide|fentanil/i.test(component.name)) return ["rebaixamento de consciência, respiração lenta/irregular ou cianose"];
    if (/benzoca/i.test(component.name)) return ["cianose/hipoxemia ou dispneia desproporcional; considerar meta-hemoglobinemia se exposição compatível"];
    if (/lidoca|proca[ií]na/i.test(component.name)) return ["convulsão, síncope ou arritmia em contexto compatível com toxicidade sistêmica por anestésico local"];
    return [];
  }));

  const limitations = [
    "Perfil representa um lote/amostra declarada, não confirmação química independente.",
    "Composição desconhecida não significa ausência de adulterantes; componente não listado não deve ser tratado como ausente.",
    "Pureza maior aumenta a fração do princípio ativo por massa do material, mas não permite prever euforia nem torna a amostra globalmente mais segura.",
    "A redução de um risco específico só pode ser sustentada se o componente relevante tiver sido adequadamente pesquisado; nenhum teste cobre todos os contaminantes.",
    "A previsão clínica depende também de massa realmente consumida, via, horário, sono, tolerância, comorbidades e coexposições.",
    ...(profileIsCocaine(profile) && !isLabVerified ? ["A composição adulterante não é considerada confirmada por este modelo; componentes relatados/suspeitos aparecem separados dos achados analíticos."] : []),
  ];

  const labProfileComplete = fullPanelEvidence &&
    profile.purity_status === "tested";
  const reportedCompositionSupportsModerateConfidence =
    profile.composition_status === "reported" &&
    profile.composition_confidence !== "baixa" &&
    compositionFindings.some((component) => component.confidence !== "baixa");
  const estimatedPuritySupportsModerateConfidence =
    profile.purity_status === "estimated" && purity != null;
  const confidence: ComponentConfidence = labProfileComplete
    ? "alta"
    : reportedCompositionSupportsModerateConfidence || estimatedPuritySupportsModerateConfidence
      ? "moderada"
      : "baixa";

  const formDetail = profile.material_form ? " (" + profile.material_form + ")" : "";
  return {
    title: "Perfil de material: " + profile.profile_name,
    summary: profileIsCocaine(profile)
      ? activeMassMg != null
        ? "Exposição calculada a partir da massa registrada e da pureza indicada: aproximadamente " + activeMassMg.toFixed(1) + " mg de cocaína-equivalente. É uma estimativa de composição, não uma previsão de intensidade subjetiva ou segurança."
        : "Pureza/massa ativa insuficientemente estabelecida para estimar a fração de cocaína; manter como exposição de composição incerta."
      : "Perfil" + formDetail + " associado a " + profile.canonical_substance_name + "; interpretação restrita aos componentes e à confiança declarados.",
    activeMassMg,
    confidence,
    evidenceBasis,
    potentialEffects,
    specificRisks,
    redFlags,
    limitations,
    compositionFindings,
  };
}

export function cloneMaterialProfileSnapshot(profile: MaterialProfile | null | undefined) {
  if (!profile) return null;
  return JSON.parse(JSON.stringify({
    id: profile.id ?? null,
    canonical_substance_name: profile.canonical_substance_name,
    profile_name: profile.profile_name,
    sample_label: profile.sample_label ?? null,
    sample_kind: profile.sample_kind ?? "lote",
    material_form: profile.material_form ?? null,
    source_context: profile.source_context ?? null,
    source_region: profile.source_region ?? null,
    purity_status: profile.purity_status ?? "unknown",
    purity_percent: profile.purity_percent ?? null,
    purity_method: profile.purity_method ?? null,
    purity_assessed_at: profile.purity_assessed_at ?? null,
    composition_status: profile.composition_status ?? "unknown",
    composition_method: profile.composition_method ?? null,
    composition_tested_at: profile.composition_tested_at ?? null,
    composition_panel: profile.composition_panel ?? null,
    composition_lab: profile.composition_lab ?? null,
    composition_coverage_notes: profile.composition_coverage_notes ?? null,
    composition_confidence: profile.composition_confidence ?? "baixa",
    composition: Array.isArray(profile.composition) ? profile.composition : [],
    risk_summary: profile.risk_summary ?? null,
    clinical_modifiers: profile.clinical_modifiers ?? {},
    notes: profile.notes ?? null,
  }));
}
